import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { CalendarDays, MapPin, Ticket } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

export default function EmbedEventCard() {
  const { slug, productSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const rootRef = useRef(null);

  // Embed lives in an iframe: make the document transparent so only the rounded card shows.
  useEffect(() => {
    const bodyBg = document.body.style.background;
    const htmlBg = document.documentElement.style.background;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => { document.body.style.background = bodyBg; document.documentElement.style.background = htmlBg; };
  }, []);

  useEffect(() => {
    api.get(`/store/${slug}/events/${productSlug}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Event not found"));
  }, [slug, productSlug]);

  // Auto-resize: tell the parent page how tall we are so the iframe never scrolls.
  useEffect(() => {
    const post = () => {
      const h = rootRef.current ? rootRef.current.offsetHeight : document.body.scrollHeight;
      if (window.parent !== window) {
        window.parent.postMessage({ type: "ticketspro-embed-height", path: window.location.pathname, height: h }, "*");
      }
    };
    post();
    const t = setTimeout(post, 300);
    let ro;
    if (rootRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(post);
      ro.observe(rootRef.current);
    }
    window.addEventListener("load", post);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); window.removeEventListener("load", post); };
  }, [data, error]);

  if (error) return <div ref={rootRef} style={{ fontFamily: "system-ui, sans-serif", padding: 16, color: "#5a6b80" }}>{error}</div>;
  if (!data) return <div ref={rootRef} style={{ padding: 16 }} />;

  const b = data.business;
  const p = data.product;
  const brand = b.brand_color || "#16243b";
  const btnColor = b.button_color || "#2ec9c9";
  const textColor = b.text_color || "#16243b";
  const eventUrl = `${window.location.origin}/store/${b.slug}/events/${p.slug}`;
  const tiers = p.ticket_tiers || [];
  const prices = tiers.length ? tiers.map((t) => t.price) : [p.price];
  const from = Math.min(...prices);
  const multiPrice = new Set(prices).size > 1;

  let cta = "Get tickets", ctaDisabled = false;
  if (p.cancelled) { cta = "Cancelled"; ctaDisabled = true; }
  else if (p.ended) { cta = "Event ended"; ctaDisabled = true; }
  else if (p.tickets_disabled) { cta = "More info"; }
  else if (p.sold_out) { cta = "Sold out"; ctaDisabled = true; }
  else if (from <= 0) { cta = "Get free ticket"; }

  const priceLabel = p.tickets_disabled ? "Free event" : from <= 0 ? "Free" : `${multiPrice ? "from " : ""}£${from.toFixed(2)}`;

  return (
    <div ref={rootRef} data-testid="embed-event-card"
      style={{ fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif", boxSizing: "border-box", width: "100%", padding: 4 }}>
      <div style={{ display: "flex", flexDirection: "column", background: "#fff", border: "1px solid #e7ecf3", borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 20px rgba(20,30,55,0.08)", maxWidth: 380 }}>
        <div style={{ height: 150, background: brand, position: "relative", overflow: "hidden" }}>
          {p.image_url
            ? <img src={p.image_url} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Ticket color="rgba(255,255,255,0.7)" size={34} /></div>}
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: textColor, lineHeight: 1.3 }}>{p.title}</div>
          {p.event_date && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: textColor, fontWeight: 500 }}>
              <CalendarDays size={15} color="#2ec9c9" /> {fmtDate(p.event_date)}
            </div>
          )}
          {p.venue_name && (
            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5a6b80" }}>
              <MapPin size={15} color="#2ec9c9" /> {p.venue_name}
            </div>
          )}
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: textColor }}>{priceLabel}</span>
            <a href={eventUrl} target="_blank" rel="noopener noreferrer" data-testid="embed-cta"
              style={{
                textDecoration: "none", fontSize: 14, fontWeight: 600, color: "#fff", borderRadius: 999, padding: "9px 18px",
                background: ctaDisabled ? "#9aa7b8" : btnColor, pointerEvents: ctaDisabled ? "none" : "auto", whiteSpace: "nowrap",
              }}>{cta}</a>
          </div>
        </div>
        <div style={{ padding: "8px 16px", borderTop: "1px solid #f0f3f7", fontSize: 11, color: "#9aa7b8", display: "flex", justifyContent: "space-between" }}>
          <span>{b.name}</span>
          <a href={`${window.location.origin}/store/${b.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: "#9aa7b8", textDecoration: "none" }}>Powered by TicketsPro</a>
        </div>
      </div>
    </div>
  );
}
